import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineArrowUp } from "react-icons/ai";
import { useCart } from '../../../context/CartContext.js';
import './PopularProduct.css';
import { fetchAllMenuItems } from '../../../api/menuApi';
import { popularDishesPlaceholder } from '../../../data/popularDishesPlaceholder.js';
import FoodPreviewModal from '../../FoodPreviewModal/FoodPreviewModal.jsx';
import ProductCard from '../../ProductCard/ProductCard';
import Button from '../../UI/Button/Button';
import LanguageContext from '../../../context/LanguageContext.js';
import { useStorePath } from '../../../lib/storefront/storePath.js';

const PopularDishes = ({ navigate }) => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const storePath = useStorePath();
    const { addToCart, addToWishlist, isInWishlist } = useCart();
    const [selectedItem, setSelectedItem] = useState(null);
    const [popularItems, setPopularItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadPopularItems = async () => {
            try {
                setLoading(true);
                const data = await fetchAllMenuItems({ limit: 24, availability: true, status: 'active', isPopular: true });
                const items = data.menuItems || data || [];
                setPopularItems(items.length > 0 ? items : popularDishesPlaceholder);
            } catch (err) {
                console.error('Error loading popular dishes:', err);
                setPopularItems(popularDishesPlaceholder);
            } finally {
                setLoading(false);
            }
        };

        loadPopularItems();
    }, []);

    const openPreview = (item) => setSelectedItem(item);
    const closePreview = () => setSelectedItem(null);

    const handleAddToCart = (item, event) => {
        event.stopPropagation();
        const variants = Array.isArray(item.variants) ? item.variants : [];
        if (variants.length > 0) {
            const v = variants.find((x) => x.isAvailable) || variants[0];
            addToCart({ ...item, selectedVariant: v, price: v.price });
            return;
        }
        addToCart(item);
    };

    const handleToggleWishlist = (item, event) => {
        event.stopPropagation();
        const wishlistIdentifier = item._id || item.id;
        if (isInWishlist(wishlistIdentifier)) {
            return;
        } else {
            addToWishlist(item);
        }
    };

    return (
        <section
            className="section section--surface-2"
            id="popular-dishes"
            dir={dir()}
        >
            <h2 className="section-title-center">
                {t('popularDishes.title')}
            </h2>

            <p className="product-description">
                {t('popularDishes.description')}
            </p>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                    <div className="menu-loading-spinner"></div>
                </div>
            ) : popularItems.length > 0 ? (
                <div className="container product-container  grid">
                    {popularItems.map(item => (
                        <ProductCard
                            key={item._id || item.id}
                            item={item}
                            currentLanguage={currentLanguage}
                            t={t}
                            onPreview={openPreview}
                            onAddToCart={handleAddToCart}
                            onToggleWishlist={handleToggleWishlist}
                            isInWishlist={isInWishlist}
                        />
                    ))}
                </div>
            ) : (
                <p className="product-description" style={{ textAlign: 'center', marginTop: '1rem' }}>
                    {t('popularDishes.empty')}
                </p>
            )}

            <div className='popular-discover-button'>
                <Button flex onClick={() => navigate(storePath(`/${currentLanguage}/menu`))}>
                    {t('home.exploreMenu')}
                    <AiOutlineArrowUp className="arrow-up-next"/>
                </Button>
            </div>
            <FoodPreviewModal
                item={selectedItem}
                isOpen={Boolean(selectedItem)}
                onClose={closePreview}
                currentLanguage={currentLanguage}
                t={t}
            />
        </section>
    );
};

export default PopularDishes;
